import { useCallback, useEffect, useState } from "react";
import type { GetToken } from "./eqmsApi";
import {
  addDocComment,
  fetchRevisionBytes,
  friendlyMessage,
  listDocAudit,
  listDocComments,
  listDocuments,
  listTraceLinks,
} from "./eqmsApi";
import { listIssues } from "./projectApi";
import type { AuditEvent, DocComment, TraceEndpointType, TraceLinkType } from "../models/eqms";

/** The tabbed sections added below the document-detail metadata: inline preview, audit trail, comments. */
export type DocDetailTab = "preview" | "activity" | "comments";
/** Which inline renderer the current revision maps to. */
export type PreviewKind = "pdf" | "image" | "docx" | "xlsx" | "text" | "unsupported";

/** A resolved trace link for the single-document panel (the far endpoint is labelled for display). */
export interface DocTraceRow {
  linkId: string;
  dir: "in" | "out";
  linkType: TraceLinkType;
  note: string;
  endpointType: TraceEndpointType;
  endpointLabel: string;
}

const IMAGE_FORMATS = new Set(["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg", "avif"]);
const TEXT_FORMATS = new Set(["md", "txt", "csv", "json", "xml", "yaml", "yml", "log", "sql", "html", "htm"]);

/** Picks an inline renderer from the revision filename (falling back to the document format). */
export function detectPreviewKind(
  filename: string | null | undefined,
  format: string | null | undefined,
): PreviewKind {
  const name = (filename || "").toLowerCase();
  const fmt = (format || "").toLowerCase();
  if (name.endsWith(".pdf") || fmt.includes("pdf")) return "pdf";
  if (/\.(png|jpe?g|gif|webp|bmp|svg|avif)$/.test(name) || fmt.startsWith("image") || IMAGE_FORMATS.has(fmt)) {
    return "image";
  }
  if (name.endsWith(".docx") || fmt.includes("docx") || fmt.includes("word")) return "docx";
  if (name.endsWith(".xlsx") || name.endsWith(".xls") || fmt.includes("xlsx") || fmt.includes("xls") || fmt.includes("excel") || fmt.includes("sheet")) {
    return "xlsx";
  }
  // Plain text last: these are the formats the diff already treats as readable, so a document you can
  // compare line by line is also a document you can read without downloading it.
  if (/\.(md|txt|csv|json|xml|ya?ml|log|sql|html?)$/.test(name) || TEXT_FORMATS.has(fmt)) return "text";
  return "unsupported";
}

/**
 * Orchestration for the document-detail tabbed panels (preview / audit / comments) and the
 * single-document traceability panel. The view holds no data-loading logic: it renders this state
 * and calls the handlers. `docId`/`projectId` come from the eqms controller's open detail.
 */
export function useDocDetail(getToken: GetToken, docId: string | null, projectId: string | null) {
  const [tab, setTab] = useState<DocDetailTab>("preview");

  const [audit, setAudit] = useState<AuditEvent[]>([]);
  const [comments, setComments] = useState<DocComment[]>([]);
  const [traceIn, setTraceIn] = useState<DocTraceRow[]>([]);
  const [traceOut, setTraceOut] = useState<DocTraceRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [commentBusy, setCommentBusy] = useState(false);

  // Inline-preview byte cache keyed by the revision currently loaded.
  const [previewRevId, setPreviewRevId] = useState<string | null>(null);
  const [previewBytes, setPreviewBytes] = useState<ArrayBuffer | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const loadAuxiliary = useCallback(async () => {
    if (!docId) return;
    setError(null);
    try {
      const [auditRows, commentRows] = await Promise.all([
        listDocAudit(getToken, docId),
        listDocComments(getToken, docId),
      ]);
      setAudit(auditRows);
      setComments(commentRows);
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }, [getToken, docId]);

  const loadTrace = useCallback(async () => {
    if (!docId || !projectId) return;
    try {
      const [links, docs, issues] = await Promise.all([
        listTraceLinks(getToken, projectId),
        listDocuments(getToken, projectId),
        listIssues(getToken, projectId),
      ]);
      const docLabel = new Map(docs.map((d) => [d.id, d.docNumber || d.title]));
      const issueLabel = new Map(issues.map((i) => [i.id, `${i.key} ${i.summary}`.trim()]));
      const labelFor = (type: TraceEndpointType, id: string) =>
        (type === "DOCUMENT" ? docLabel.get(id) : issueLabel.get(id)) ?? id.slice(0, 8);

      const outRows: DocTraceRow[] = [];
      const inRows: DocTraceRow[] = [];
      for (const l of links) {
        if (l.sourceType === "DOCUMENT" && l.sourceId === docId) {
          outRows.push({ linkId: l.id, dir: "out", linkType: l.linkType, note: l.note, endpointType: l.targetType, endpointLabel: labelFor(l.targetType, l.targetId) });
        } else if (l.targetType === "DOCUMENT" && l.targetId === docId) {
          inRows.push({ linkId: l.id, dir: "in", linkType: l.linkType, note: l.note, endpointType: l.sourceType, endpointLabel: labelFor(l.sourceType, l.sourceId) });
        }
      }
      setTraceOut(outRows);
      setTraceIn(inRows);
    } catch {
      // Trace panel is supplementary; keep the detail view usable if it fails.
    }
  }, [getToken, docId, projectId]);

  // (Re)load whenever the open document changes; reset to the preview tab + drop stale preview bytes.
  useEffect(() => {
    setTab("preview");
    setAudit([]);
    setComments([]);
    setTraceIn([]);
    setTraceOut([]);
    setPreviewRevId(null);
    setPreviewBytes(null);
    setPreviewError(null);
    void loadAuxiliary();
    void loadTrace();
  }, [docId, loadAuxiliary, loadTrace]);

  const addComment = useCallback(async (body: string) => {
    if (!docId || !body.trim()) return;
    setCommentBusy(true);
    setError(null);
    try {
      await addDocComment(getToken, docId, body.trim());
      setComments(await listDocComments(getToken, docId));
    } catch (e) {
      setError(friendlyMessage(e));
    } finally {
      setCommentBusy(false);
    }
  }, [getToken, docId]);

  /** Fetches the given revision's bytes for inline preview (cached per revision id). */
  const ensurePreview = useCallback(async (revId: string | null) => {
    if (!docId || !revId) {
      setPreviewBytes(null);
      setPreviewRevId(null);
      return;
    }
    if (revId === previewRevId && previewBytes) return;
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const bytes = await fetchRevisionBytes(getToken, docId, revId);
      setPreviewBytes(bytes);
      setPreviewRevId(revId);
    } catch (e) {
      setPreviewBytes(null);
      setPreviewError(friendlyMessage(e));
    } finally {
      setPreviewLoading(false);
    }
  }, [getToken, docId, previewRevId, previewBytes]);

  return {
    tab, setTab,
    audit, comments, traceIn, traceOut, error,
    commentBusy, addComment,
    reload: loadAuxiliary,
    previewBytes, previewLoading, previewError, ensurePreview,
  };
}

export type DocDetailController = ReturnType<typeof useDocDetail>;
